import { cloneElement, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { addMonthsIso } from '../utils/monthRange';
import './MonthSlide.css';

// One-month-at-a-time stepping for month-column CSS grids, with the columns sliding over by one.
//
// How it fits together:
// - The grid gets a 0px track either side of its months (monthSlideColumns) and each row renders its
//   month cells through MonthSlideCells, which parks the neighbouring month's cell in those tracks,
//   overflowing just out of view.
// - The first column's cells get the `ppc-month-slide-pin` class so the months pass underneath them.
// - Every grid sits inside the element holding useMonthSlide's containerRef and carries the
//   `ppc-month-slide-grid` class.
// - A step slides every month cell one column over, carrying the neighbour into view, then swaps in
//   the new window.

const MONTH_SLIDE_MS = 160;

// How many months either side of the window to keep loaded, so stepping through them is instant.
const MONTH_SLIDE_PREFETCH_RANGE = 6;

// Every month of each calendar year the given months touch. Fetching whole years keeps the query
// keys the same while stepping through a year, so most steps need no request at all.
const getYearSpanMonths = (months) => {
  const years = Array.from(
    new Set((Array.isArray(months) ? months : []).map((m) => String(m || '').slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))
  ).sort();
  const out = [];
  for (const year of years) {
    for (let i = 1; i <= 12; i += 1) out.push(`${year}-${String(i).padStart(2, '0')}-01`);
  }
  return out;
};

// The months to fetch for the window starting at `start`: whole years covering the window plus the
// neighbouring month on each side (which the slide shows on its way in).
export const getMonthSlideFetchMonths = (start, windowSize) =>
  getYearSpanMonths([addMonthsIso(start, -1), addMonthsIso(start, windowSize)]);

// The distinct fetch lists for every window within MONTH_SLIDE_PREFETCH_RANGE steps of `start`.
export const getMonthSlidePrefetchMonthLists = (start, windowSize) => {
  const byKey = new Map();
  for (let d = -MONTH_SLIDE_PREFETCH_RANGE; d <= MONTH_SLIDE_PREFETCH_RANGE; d += 1) {
    const list = getMonthSlideFetchMonths(addMonthsIso(start, d), windowSize);
    if (list.length > 0) byKey.set(list.join(','), list);
  }
  return Array.from(byKey.values());
};

// gridTemplateColumns for a grid using MonthSlideCells.
export const monthSlideColumns = (firstColumn, months, monthColumn) =>
  `${firstColumn} 0px ${months.map(() => monthColumn).join(' ')} 0px`;

const withClass = (element, className) =>
  cloneElement(element, { className: [element.props.className, className].filter(Boolean).join(' ') });

function MonthSlideGhost({ side, month, renderCell }) {
  return (
    <div className={`ppc-month-slide-col ppc-month-slide-ghost ppc-month-slide-ghost--${side}`} inert aria-hidden="true">
      {month ? withClass(renderCell(month), 'ppc-month-slide-ghost__cell') : null}
    </div>
  );
}

// One row's month cells, plus the months either side parked just out of view. `renderCell(month)`
// returns the cell element exactly as the row would render it.
export function MonthSlideCells({ months, prevMonth, nextMonth, renderCell }) {
  return (
    <>
      <MonthSlideGhost side="prev" month={prevMonth} renderCell={renderCell} />
      {months.map((m) => withClass(renderCell(m), 'ppc-month-slide-col'))}
      <MonthSlideGhost side="next" month={nextMonth} renderCell={renderCell} />
    </>
  );
}

// Steps `start` by one month per call, sliding the grids under `containerRef` as it goes. The slide is
// driven straight on the DOM rather than through state, so it starts on the click instead of waiting
// for the page to re-render. `prepare(nextStart)` (optional) can return a promise, e.g. a prefetch;
// the new window is shown once both it and the slide have finished. Steps made mid-slide queue up.
export function useMonthSlide({ start, setStart, prepare }) {
  const containerRef = useRef(null);
  const slideRef = useRef(null);
  const queuedStepsRef = useRef(0);

  const endSlide = useCallback(() => {
    const slide = slideRef.current;
    if (!slide) return;
    slideRef.current = null;
    containerRef.current?.classList.remove(slide.className);
  }, []);

  // Drop the slide offset in the same commit that renders the new window (before paint), so the old
  // months never flash back into place. Any change of start ends a slide.
  useLayoutEffect(() => {
    if (slideRef.current && start !== slideRef.current.from) endSlide();
  }, [endSlide, start]);

  useEffect(() => () => {
    endSlide();
    queuedStepsRef.current = 0;
  }, [endSlide]);

  const stepMonths = (delta) => {
    if (slideRef.current) {
      queuedStepsRef.current += delta;
      return;
    }
    const target = addMonthsIso(start, delta);
    if (!target) return;

    const container = containerRef.current;
    const grids = container ? Array.from(container.querySelectorAll('.ppc-month-slide-grid')) : [];
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (grids.length === 0 || reduceMotion) {
      setStart(target);
      return;
    }

    // Measuring also flushes styles, so a slide straight after the previous one starts from rest.
    for (const grid of grids) {
      const cell = grid.querySelector('.ppc-month-slide-col:not(.ppc-month-slide-ghost)');
      if (cell) grid.style.setProperty('--ppc-month-slide-w', `${cell.getBoundingClientRect().width}px`);
    }
    container.style.setProperty('--ppc-month-slide-ms', `${MONTH_SLIDE_MS}ms`);

    const slide = { from: start, className: `ppc-month-slide--${delta < 0 ? 'prev' : 'next'}` };
    slideRef.current = slide;
    container.classList.add(slide.className);

    const slideFinished = new Promise((resolve) => {
      setTimeout(resolve, MONTH_SLIDE_MS);
    });
    const ready = Promise.resolve(prepare?.(target)).catch(() => {});

    Promise.all([slideFinished, ready]).then(() => {
      if (slideRef.current !== slide) return;
      setStart(target);
      // Safety net: never leave the columns shifted if start somehow doesn't change.
      setTimeout(() => {
        if (slideRef.current === slide) endSlide();
      }, 1000);
    });
  };

  // Play queued steps one month at a time, each from the window the previous one landed on.
  useEffect(() => {
    const queued = queuedStepsRef.current;
    if (slideRef.current || queued === 0) return;
    const delta = Math.sign(queued);
    queuedStepsRef.current -= delta;
    stepMonths(delta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  return { containerRef, stepMonths };
}
